import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ApiClient,
  type ProducerCapabilities,
  type ProducerInvitation,
  type ProducerMembership,
  type ProducerOnboardingRequest,
  type ProducerProfileDto,
  type ProducerProfileResponse,
  type ProducerTierDto,
  type StaffAction,
} from '../api/api.client';
import { allows } from '../domain/producer-roles';

/**
 * État du profil producteur du compte connecté.
 *
 * Partagé entre les écrans plutôt que rechargé par chacun : le bouton « Vendre
 * sur Champ'Table » des paramètres, le formulaire d'inscription et le tableau
 * de bord parlent tous du même profil, et doivent afficher le même statut au
 * même instant. Deux appels concurrents à `/me/producteur` finiraient par
 * montrer des états différents dans deux coins de l'écran.
 */
@Injectable({ providedIn: 'root' })
export class ProducerService {
  private readonly api = inject(ApiClient);

  private readonly state = signal<ProducerProfileResponse | undefined>(undefined);
  private readonly tierState = signal<ProducerTierDto | undefined>(undefined);
  private readonly pending = signal(false);
  private readonly tierPending = signal(false);
  private readonly failure = signal<string | undefined>(undefined);
  private readonly tierFailure = signal<string | undefined>(undefined);

  /** `undefined` tant que rien n'a été chargé, `null` pour un simple client. */
  readonly profile = computed<ProducerProfileDto | null | undefined>(
    () => this.state()?.profile ?? (this.state() === undefined ? undefined : null),
  );

  readonly capabilities = computed<ProducerCapabilities | null>(
    () => this.state()?.capabilities ?? null,
  );

  /** Rôle du compte sur l'exploitation, et les actions qu'il autorise. */
  readonly membership = computed<ProducerMembership | null>(() => this.state()?.membership ?? null);

  readonly invitations = computed<readonly ProducerInvitation[]>(
    () => this.state()?.invitations ?? [],
  );

  readonly loading = this.pending.asReadonly();
  readonly error = this.failure.asReadonly();
  readonly tier = this.tierState.asReadonly();
  readonly tierLoading = this.tierPending.asReadonly();
  readonly tierError = this.tierFailure.asReadonly();
  readonly loaded = computed(() => this.state() !== undefined);

  /** Un profil existe-t-il, quel que soit son avancement ? */
  readonly hasProfile = computed(() => this.profile() != null);

  /**
   * Le rôle du compte autorise-t-il cette action ?
   *
   * On consulte les droits renvoyés par le serveur, sans rejouer la matrice :
   * une seconde copie de la règle finirait par diverger, et la divergence se
   * verrait d'abord sur un écran qui propose une action que l'API refusera.
   */
  can(action: StaffAction): boolean {
    return allows(this.membership()?.actions, action);
  }

  private inFlight: Promise<void> | undefined;

  /**
   * Charge le profil si ce n'est pas déjà fait.
   *
   * Les appels simultanés partagent la même promesse : l'en-tête et l'écran
   * courant demandent souvent le profil dans la même microtâche.
   */
  async ensureLoaded(): Promise<void> {
    if (this.state() !== undefined) {
      return;
    }
    await this.refresh();
  }

  async refresh(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.pending.set(true);
    this.failure.set(undefined);
    this.inFlight = (async () => {
      try {
        this.state.set(await firstValueFrom(this.api.producerProfile()));
      } catch (error) {
        this.failure.set(describeError(error, 'Impossible de lire votre profil producteur.'));
      } finally {
        this.pending.set(false);
        this.inFlight = undefined;
      }
    })();

    return this.inFlight;
  }

  async refreshTier(): Promise<void> {
    if (!this.hasProfile()) {
      this.tierState.set(undefined);
      return;
    }
    this.tierPending.set(true);
    this.tierFailure.set(undefined);
    try {
      const response = await firstValueFrom(this.api.producerTier());
      this.tierState.set(response.tier);
    } catch (error) {
      this.tierFailure.set(describeError(error, 'Impossible de lire votre palier.'));
    } finally {
      this.tierPending.set(false);
    }
  }

  async downgradeTier(level: number): Promise<{ readonly ok: true } | OnboardingFailure> {
    try {
      const response = await firstValueFrom(this.api.downgradeProducerTier(level));
      this.tierState.set(response.tier);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible de programmer la descente de palier.'),
      };
    }
  }

  async cancelTierDowngrade(): Promise<{ readonly ok: true } | OnboardingFailure> {
    try {
      const response = await firstValueFrom(this.api.cancelProducerTierDowngrade());
      this.tierState.set(response.tier);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible d’annuler la descente de palier.'),
      };
    }
  }

  async boostTier(level: number): Promise<{ readonly ok: true } | OnboardingFailure> {
    try {
      const response = await firstValueFrom(this.api.boostProducerTier(level));
      this.tierState.set(response.tier);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible de débloquer ce palier.'),
      };
    }
  }

  /**
   * Enregistre le formulaire d'inscription.
   *
   * L'erreur est renvoyée à l'appelant plutôt que stockée : le formulaire a
   * besoin du code fonctionnel pour désigner le champ fautif, ce qu'un message
   * global ne permet pas.
   */
  async submitOnboarding(
    body: ProducerOnboardingRequest,
  ): Promise<
    { readonly ok: true; readonly response: ProducerProfileResponse } | OnboardingFailure
  > {
    try {
      const response = await firstValueFrom(this.api.saveProducerProfile(body));
      this.state.set(response);
      return { ok: true, response };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible d’enregistrer votre profil producteur.'),
      };
    }
  }

  /**
   * Ouvre le contrôle d'identité et renvoie l'URL du formulaire hébergé.
   *
   * Le profil local est rafraîchi au passage : il vient de basculer en
   * `payment_pending`, et le tableau de bord doit le refléter au retour.
   */
  async startPaymentOnboarding(): Promise<
    { readonly ok: true; readonly onboardingUrl: string } | OnboardingFailure
  > {
    try {
      const response = await firstValueFrom(this.api.startProducerPayment());
      const previous = this.state();
      if (previous === undefined) {
        // Cas théorique : on n'ouvre un compte de paiement que depuis le tableau
        // de bord, donc après chargement. Le rôle n'étant pas dans cette
        // réponse, on va le rechercher plutôt que d'inventer un rôle vide, qui
        // masquerait toute l'interface.
        await this.refresh();
      } else {
        this.state.set({
          ...previous,
          profile: response.profile,
          capabilities: response.capabilities,
          message: null,
        });
      }
      return { ok: true, onboardingUrl: response.onboardingUrl };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible d’ouvrir la configuration des paiements.'),
      };
    }
  }

  /**
   * Accepte ou refuse une invitation à rejoindre une exploitation.
   *
   * L'état est rechargé ensuite plutôt que rapiécé : accepter change le
   * rattachement actif du compte, donc le profil, le rôle et les droits d'un
   * seul coup.
   */
  async respondToInvitation(
    profileId: string,
    accept: boolean,
  ): Promise<{ readonly ok: true } | OnboardingFailure> {
    try {
      await firstValueFrom(this.api.respondToInvitation(profileId, accept));
      this.state.set(undefined);
      await this.refresh();
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        code: errorCode(error),
        message: describeError(error, 'Impossible de répondre à cette invitation.'),
      };
    }
  }

  /** Oublie l'état chargé, à la déconnexion : un autre compte n'a pas le même profil. */
  clear(): void {
    this.state.set(undefined);
    this.tierState.set(undefined);
    this.failure.set(undefined);
    this.tierFailure.set(undefined);
  }
}

export interface OnboardingFailure {
  readonly ok: false;
  /** Code fonctionnel de l'API (`SIRET_CLOSED`, `CGU_REQUIRED`…), quand il existe. */
  readonly code: string | undefined;
  readonly message: string;
}

interface ApiErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string };
}

function errorCode(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse) {
    const code = (error.error as ApiErrorBody | null)?.error?.code;
    return typeof code === 'string' && code.length > 0 ? code : undefined;
  }
  return undefined;
}

/**
 * Message affichable.
 *
 * Le backend rédige déjà des messages destinés au producteur, et ils sont plus
 * précis que tout ce qu'on pourrait deviner ici : on les reprend tels quels
 * quand ils sont présents.
 */
function describeError(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const message = (error.error as ApiErrorBody | null)?.error?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
    if (error.status === 0) {
      return 'Connexion perdue. Vérifiez votre réseau et réessayez.';
    }
  }
  return fallback;
}
